import React from 'react';
import { Box, Paper, Stack, Typography, Divider, Grid2 } from '@mui/material';
import { RefundHistoryItem } from './Refund.helper';
import { ReportFooterTimestamp } from '../../../utils/reportUtils';
import { formatMoney } from '../../../utils/commonUtils';
import { formatDateTimeAmPm } from '../../../utils/dateUtils';

export interface AdvanceRefundReceiptComponentProps {
  data: RefundHistoryItem;
  currency?: string;
}

// Reusable row component for label-value display
const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Grid2 container size={{ xs: 12 }} sx={{ mb: 0.7, alignItems: 'center' }}>
    <Grid2 sx={{ width: 150, minWidth: 90, textAlign: 'left', pr: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: 15, letterSpacing: 0.1 }}>{label}</Typography>
    </Grid2>
    <Grid2 sx={{ flex: 1 }}>
      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600, fontSize: 15, letterSpacing: 0.1 }}>{value ?? '-'}</Typography>
    </Grid2>
  </Grid2>
);

const AdvanceRefundReceiptComponent: React.FC<AdvanceRefundReceiptComponentProps> = ({ data, currency = 'INR' }) => {
  return (
    <>
      <Box data-testid="advance-refund-receipt-component">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            my: 3,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to right, transparent, #e0e0e0)' }} />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 2,
              color: '#333',
            }}
          >
            Advance Refund Receipt
          </Typography>
          <Box sx={{ flex: 1, height: 2, background: 'linear-gradient(to left, transparent, #e0e0e0)' }} />
        </Box>

        <Paper elevation={0} sx={{ px: 4, borderRadius: 3, mb: 5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} divider={<Divider orientation="vertical" flexItem />}>
            {/* Refund Details */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 700, mb: 2, letterSpacing: 0.2 }}>Refund Details</Typography>
              <Stack spacing={1.0}>
                <InfoRow label={'Receipt No:'} value={data.receipt_number || '-'} />
                <InfoRow label={'Bill/Advance No:'} value={data.bill_advance_number || '-'} />
                <InfoRow label={'Refund Type:'} value={data.refund_type || '-'} />
                <InfoRow label={'Status:'} value={data.status || '-'} />
                <InfoRow label={'Reason:'} value={data.refund_reason || '-'} />
                <InfoRow label={'Processed By:'} value={data.history_user?.full_name || '-'} />
              </Stack>
            </Box>

            {/* Amount Summary */}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 700, mb: 2, letterSpacing: 0.2 }}>Amount Summary</Typography>
              <Stack spacing={1.0}>
                <InfoRow label={'Date and Time:'} value={formatDateTimeAmPm(data.history_date)} />
                <InfoRow label={'Total Amount:'} value={formatMoney(Number(data.total_amount) || 0, currency)} />
                <InfoRow label={'Concession:'} value={formatMoney(Number(data.concession_amount) || 0, currency)} />
                <InfoRow label={'Net Amount:'} value={formatMoney(Number(data.net_amount) || 0, currency)} />
                <InfoRow label={'Requested Amt:'} value={formatMoney(Number(data.requested_amount) || 0, currency)} />
                <InfoRow label={'Refund Amt:'} value={formatMoney(Number(data.refund_amount) || 0, currency)} />
              </Stack>
            </Box>
          </Stack>
          <Divider sx={{ my: 4, borderColor: '#1a1a1a' }} />

          {/* Signature */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 5, py: 4 }}>
            <Box sx={{ width: 280, textAlign: 'center' }}>
              <Box sx={{ borderTop: '2px dotted', borderColor: 'text.secondary', mb: 0.75 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Issuer Signature</Typography>
            </Box>
          </Box>

          <ReportFooterTimestamp />
        </Paper>
      </Box>
    </>
  );
};

export default AdvanceRefundReceiptComponent;
